import {Injectable} from '@angular/core';
import {CheckoutEntryValidationService} from '@app/checkout/services/checkout-entry-validation.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpOrderEntryService} from '@app/services/dcp-order-entry.service';
import {
  ArrayService,
  ConfiguratorUriParameterService,
  DcpEntryGroup,
  ProviderContractSplitGroup
} from '@dcp/configurator';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Cart} from '@spartacus/cart/base/root';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {cloneDeep} from 'lodash-es';
import {Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {DcpProductService} from './dcp-product.service';

export const PRODUCT_BUNDLE_TYPE: string = 'PRODUCT';
export const DERIVATIVE_BUNDLE_TYPE: string = 'DERIVATIVE';
export const ROOT_BUNDLE_TYPE: string = 'ROOT';
export const TRANSACTION_TYPE_BUNDLE_TYPE: string = 'TRANSACTION_TYPE';
export const CONTRACT_BUNDLE_TYPE: string = 'CONTRACT';
export const DATE_BUNDLE_TYPE: string = 'DATE';
export const VENDOR_BUNDLE_TYPE: string = 'VENDOR';
export const EDITION_BUNDLE_TYPE: string = 'EDITION';
export const ADDON_BUNDLE_TYPE: string = 'ADDON';

@Injectable({
  providedIn: 'root'
})
@UntilDestroy()
export class EntryGroupService {

  constructor(
    private activeCartService: DcpActiveCartService,
    private uriParamService: ConfiguratorUriParameterService,
    private productService: DcpProductService,
    private checkoutEntryValidationService: CheckoutEntryValidationService,
    private orderEntryService: DcpOrderEntryService,
  ) {
  }

  isDerivative = eg => eg?.displayType === DERIVATIVE_BUNDLE_TYPE;

  isProduct = eg => eg?.displayType === PRODUCT_BUNDLE_TYPE;

  isRoot = eg => eg?.displayType === ROOT_BUNDLE_TYPE;

  isEdition = eg => eg?.displayType === EDITION_BUNDLE_TYPE;

  isAddon = eg => eg?.displayType === ADDON_BUNDLE_TYPE;

  /**
   * Returns a deep copy of sorted EntryGroups with displayType VENDOR
   *
   * @param entryGroups List of DcpEntryGroups from cart.
   */
  public getProviderGroups(entryGroups: DcpEntryGroup[]): ProviderContractSplitGroup[] {
    const providerSplitGroups: ProviderContractSplitGroup[] = [];
    if (entryGroups?.length) {
      cloneDeep(entryGroups).forEach(entryGroup => {
        if (entryGroup?.displayType === VENDOR_BUNDLE_TYPE) {
          const splitGroup: ProviderContractSplitGroup = this.getSplitGroup(providerSplitGroups, entryGroup);
          // sort Edition Entry Groups
          this.sortParentEntriesByItsEditionGroups(this.isRoot, entryGroup);
          // sort Root Entry Groups
          this.sortParentEntriesByItsEditionGroups(eg => this.isDateBundleType(eg?.displayType), entryGroup);
          // sort Contract Entry Groups
          this.sortCurrentEntryByItsEditions(entryGroup.entryGroups);
          splitGroup.entryGroups.push(entryGroup);
          this.sortCurrentEntryByItsEditions(splitGroup.entryGroups);
        }
      });
    }
    this.sortCurrentEntryByItsEditions(providerSplitGroups);
    return providerSplitGroups;
  }

  /**
   * Searches for the first entry group with displayType = type and returns its first entry.
   *
   * @param entryGroups list of entryGroups to search in.
   * @param type displayType to search for.
   */
  public getFirstEntry(entryGroups: DcpEntryGroup[], type: string = PRODUCT_BUNDLE_TYPE): OrderEntry {
    const egs = this.filterByGroupType(entryGroups, type);
    if (egs?.length && egs[0]?.entries?.length) {
      return egs[0]?.entries[0];
    }
    return undefined;
  }

  public filter(entryGroups: DcpEntryGroup[], fn: (DcpEntryGroup) => boolean, collectedGroups: DcpEntryGroup[] = []) {
    entryGroups.forEach((entryGroup) => {
      if (fn(entryGroup)) {
        collectedGroups.push(entryGroup);
      }
    });
    return collectedGroups;
  }

  public filterByGroupType(entryGroups: DcpEntryGroup[], type: string = PRODUCT_BUNDLE_TYPE): DcpEntryGroup[] {
    if (entryGroups) {
      return this.collectEntryGroups(entryGroup => entryGroup.displayType === type, entryGroups, []);
    }
    return [];
  }

  isDateBundleType(displayType: string): boolean {
    return displayType === DATE_BUNDLE_TYPE;
  }

  /**
   * Loops over the entryGroups passed in and returns their entries.
   * Please note that only entryGroups with displayType PRODUCT or DERIVATIVE have entries.
   *
   * @param entryGroups to loop over.
   */
  public getAllGroupEntries(entryGroups: DcpEntryGroup[]): OrderEntry[] {
    return entryGroups.reduce((acc, x) => acc.concat(x.entries), []);
  }

  public countProviderContracts(providerGroups: ProviderContractSplitGroup[]): number {
    let count: number = 0;
    if (providerGroups?.length) {
      providerGroups.forEach((providerGroup) => {
        const contracts: DcpEntryGroup[] = this.collectEntryGroups(
          eg => this.isDateBundleType(eg?.displayType),
          providerGroup.entryGroups,
          []
        );
        count += contracts.length;
      });
    }
    return count;
  }

  /**
   * Recursively collect entries in entry groups
   *
   * @param fn filter function to find the entryGroup by
   * @param entryGroups Root node of entryGroups
   * @param collectedGroups groups already collected. Can be an empty array.
   */
  public collectEntryGroups(
    fn: (item: DcpEntryGroup) => boolean,
    entryGroups: DcpEntryGroup[],
    collectedGroups: DcpEntryGroup[] = []
  ): DcpEntryGroup[] {
    if (entryGroups) {
      for (const entryGroup of entryGroups) {
        if (fn(entryGroup)) {
          collectedGroups.push(entryGroup);
        }
        if (entryGroup?.entryGroups) {
          this.collectEntryGroups(fn, entryGroup.entryGroups, collectedGroups);
        }
      }
    }
    return collectedGroups;
  }

  /**
   * Returns an array containing all material ids of entryGroups marked as derivative.
   * The data is either collected from the URL or the cart.entryGroups, depending on
   * if the product is in the cart already.
   *
   * @param supplierAlternativeAID product.code
   */
  public getDerivativeSupplierAlternativeIds(supplierAlternativeAID: string): Observable<string[]> {
    return this.productService.isProductInCart(supplierAlternativeAID)
      .pipe(
        switchMap((isProductInCart: boolean) => {
          if (isProductInCart) {
            return this.getDerivativeSupplierAlternativeIdsFromActiveCart(supplierAlternativeAID);
          } else {
            return of(this.getDerivativeDataFromUrl());
          }
        })
      );
  }

  getDerivativeIdsFromUrl(): string[] {
    return ArrayService.removeDuplicates(this.getDerivativeDataFromUrl().map(i => i?.split('#')?.[0]));
  }

  getDerivativeDataFromUrl(): string[] {
    const derivativeProducts: string = this.uriParamService.getDerivativeProductsFromUrl();
    if (derivativeProducts) {
      return derivativeProducts.split(',');
    }
    return [];
  }

  /**
   * Collects all material ids that belong to derivatives from cart entryGroups.
   */
  public getDerivativeAlternativeIdsFromEntryGroups(entryGroups: DcpEntryGroup[]): string[] {
    const orderEntries: OrderEntry[] = this.getOrderEntriesByBundleType(entryGroups, this.isDerivative);
    return orderEntries.map(orderEntry => this.getDerivativeSupplierAlternativeAIdData(orderEntry));
  }

  public getDerivativeSupplierAlternativeAIdData(entry: OrderEntry): string {
    const configuration = this.orderEntryService.getConfiguration(entry);
    return `${entry?.product?.supplierAlternativeAID}#${configuration?.itemNumber}#${configuration?.itemId}`;
  }

  /**
   * Collects all material ids that belong to derivatives from the active cart entryGroups.
   */
  public getDerivativeSupplierAlternativeIdsFromActiveCart(supplierAlternativeAID: string): Observable<string[]> {
    return this.getRootGroupsFromActiveCart(supplierAlternativeAID).pipe(
      map((entryGroups: DcpEntryGroup[]) => this.getDerivativeAlternativeIdsFromEntryGroups(entryGroups))
    );
  }

  /**
   * Sort contract groups or providerSplitGroups by invalid entries,
   * groups with invalid entries should be on top of the list
   *
   * @param parentsEntryGroups ProviderContractSplitGroup to be sorted
   */
  sortCurrentEntryByItsEditions(parentsEntryGroups: DcpEntryGroup[] | ProviderContractSplitGroup[]): void {
    parentsEntryGroups
      .sort((eg1: DcpEntryGroup | ProviderContractSplitGroup, eg2: DcpEntryGroup | ProviderContractSplitGroup) => this.sortByInvalidEntries(
        this.getOrderEntriesByBundleType(eg1.entryGroups),
        this.getOrderEntriesByBundleType(eg2.entryGroups)
      ));
  }

  /**
   * Collects all entryGroups with the given function and collects all DcpOrderEntries stored in the DcpEntryGroup[] passed into it.
   *
   * @param entryGroups DcpEntryGroup[] that contains DcpOrderEntries or which sub entryGroups contains DcpOrderEntries.
   * @param fn filter function to find the entryGroup by
   */
  public getOrderEntriesByBundleType(entryGroups: DcpEntryGroup[], fn: (item: DcpEntryGroup) => boolean = this.isProduct): OrderEntry[] {
    if (entryGroups?.length) {
      const filteredEntryGroups: DcpEntryGroup[] = this.collectEntryGroups(
        fn,
        entryGroups, []);
      return this.getAllGroupEntries(filteredEntryGroups);
    }
    return [];
  }

  /**
   * Get the root groups of the current cart by a given supplierAlternativeId
   *
   * @param supplierAlternativeId The supplierAlternativeId to filter for
   */
  public getRootGroupsFromActiveCart(supplierAlternativeId: string): Observable<DcpEntryGroup[]> {
    return this.activeCartService.getActiveCart()
      .pipe(
        map((cart: Cart) => {
          if (cart?.entryGroups) {
            return this.getRootGroupsBySupplierAlternativeId(cart.entryGroups, supplierAlternativeId);
          }
          return [];
        })
      );
  }

  getRootGroupsBySupplierAlternativeId(contractGroup: DcpEntryGroup[], supplierAlternativeId: string): DcpEntryGroup[] {
    const roots: DcpEntryGroup[] = this.collectEntryGroups(this.isRoot, contractGroup, []);
    return roots.filter(root => this.containsChild(root, supplierAlternativeId));
  }

  public getRootGroupsByProductCode(contractGroup: DcpEntryGroup[], code: string): DcpEntryGroup[] {
    const roots: DcpEntryGroup[] = this.collectEntryGroups(this.isRoot, contractGroup, []);
    return roots.filter(root => this.containsChildWithProductCode(root, code));
  }

  public getRootGroupByEntryNumber(contractGroup: DcpEntryGroup[], itemNumber: string): DcpEntryGroup {
    const roots: DcpEntryGroup[] = this.collectEntryGroups(this.isRoot, contractGroup);
    return roots.find(root => this.containsChildWithEntryNumber(root, itemNumber));
  }

  public getRootGroupParents(entryGroups: DcpEntryGroup[]): DcpEntryGroup[] {
    return this.collectEntryGroups(
      (entryGroup) => entryGroup.entryGroups?.some(this.isRoot),
      entryGroups,
      []
    );
  }

  public getRootGroupParentByProductCode(entryGroups: DcpEntryGroup[], productCode: string): DcpEntryGroup {
    return this.getRootGroupParents(entryGroups).find((entryGroup) => entryGroup.entryGroups.find((rootGroup) => this.containsChildWithProductCode(rootGroup, productCode)));
  }

  public getProductIndexInRootParentByProductCode(entryGroups: DcpEntryGroup[], productCode: string): number {
    const rootGroups = this.collectEntryGroups(
      this.isRoot,
      this.getRootGroupParentByProductCode(entryGroups, productCode)?.entryGroups,
      []
    );
    const entries = rootGroups.reduce((acc, rootGroup) => [...acc, ...rootGroup.entryGroups], [])
      .filter(this.isProduct)
      .reduce((acc, entryGroup) => [...acc, ...entryGroup.entries], []);
    return entries.findIndex((entry) => entry.product.code === productCode);
  }

  private getSplitGroup(providerSplitGroups: ProviderContractSplitGroup[], entryGroup: DcpEntryGroup): ProviderContractSplitGroup {
    let splitGroup: ProviderContractSplitGroup = providerSplitGroups.find(group => group.externalReferenceId === entryGroup.externalReferenceId);
    if (!splitGroup) {
      splitGroup = {
        externalReferenceId: entryGroup.externalReferenceId,
        entryGroups: [],
      };
      providerSplitGroups.push(splitGroup);
    }
    return splitGroup;
  }

  private sortByInvalidEntries(eg1Entries: OrderEntry[], eg2Entries: OrderEntry[]): number {
    const e1Invalid: boolean = this.checkoutEntryValidationService.containsInvalidEntries(eg1Entries);
    const e2Invalid: boolean = this.checkoutEntryValidationService.containsInvalidEntries(eg2Entries);
    if (e1Invalid && !e2Invalid) {
      return -1;
    } else if (!e1Invalid && e2Invalid) {
      return 1;
    }
    return 0;
  }

  /**
   * Sort edition groups by invalid entries,
   * groups with invalid entries should be on top of the list
   *
   * @param parentFilterFn Filter function that selects all parent displayTypes
   * @param vendorEntryGroup vendor group
   */
  private sortParentEntriesByItsEditionGroups(parentFilterFn: (item: DcpEntryGroup) => boolean, vendorEntryGroup: DcpEntryGroup): void {
    this.collectEntryGroups(parentFilterFn, vendorEntryGroup.entryGroups, [])
      .forEach(parentEntryGroup => {
        this.sortCurrentEntryByItsEditions(parentEntryGroup.entryGroups);
      });
  }

  /**
   * Checks if a parent EntryGroup contains a child EntryGroup.
   *
   * @param parent parent EntryGroup to search in.
   * @param supplierAlternativeId supplierAlternativeId of the product in the child EntryGroup to search for.
   */
  private containsChild(parent: DcpEntryGroup, supplierAlternativeId: string): boolean {
    return this.collectEntryGroups(e => !!e.entries.length, parent.entryGroups, [])
      .some(d => d.entries.some(e => supplierAlternativeId === e?.product?.supplierAlternativeAID));
  }

  /**
   * Checks if a parent EntryGroup contains a child EntryGroup identified by its products code.
   *
   * @param parent DcpEntryGroup to search in.
   * @param code string product code in the child EntryGroup to search for.
   */
  private containsChildWithProductCode(parent: DcpEntryGroup, code: string): boolean {
    return this.collectEntryGroups(e => !!e.entries.length, parent.entryGroups, [])
      .some(d => d.entries.some(e => code === e?.product?.code));
  }

  /**
   * Checks if a parent EntryGroup contains a child EntryGroup identified by its entry's itemNumber.
   *
   * @param parent DcpEntryGroup to search in.
   * @param itemNumber string entry itemNumber in the child EntryGroup to search for.
   */
  private containsChildWithEntryNumber(parent: DcpEntryGroup, itemNumber: string): boolean {
    return this.collectEntryGroups(e => !!e.entries.length, parent.entryGroups)
      .some(entryGroup => entryGroup.entries.some(entry => entry?.entryNumber?.toString(10) === itemNumber));
  }
}
